import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useU2000AuthContext } from "../auth/U2000UseAuthContext";
import { useU2000WebSocketContext } from "../websocket/U2000WebSocketContext";
import useU2000AxiosAuthenticated from "../../axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../axios/U2000HandleApiError";
import { isNotificationMessage } from "../../types/U2000WebSocketMessages";
import type { U2000Notification } from "../../types/U2000NotificationTypes";
import type { U2000SearchPayload, U2000Pagination } from "../../components/tables/basic/U2000BasicTable";
import {
  doSearch,
  doGetUnreadCount,
  doMarkAsRead,
  doMarkAsUnread,
  doMarkAllAsRead,
  doDeleteNotification,
} from "./U2000NotificationService";
import {
  u2000IsNewNotificationId,
  u2000ShouldAcceptNotificationForOrg,
} from "../../tools/U2000NotificationWsHelpers";

export interface U2000NotificationContextType {
  /**
   * Liste des notifications chargées
   */
  notifications: U2000Notification[];
  /**
   * Nombre de notifications non lues
   */
  unreadCount: number;
  /**
   * Indique si les notifications sont en cours de chargement
   */
  isLoading: boolean;
  /**
   * Indique s'il y a une erreur
   */
  error: string | null;
  /**
   * Pagination actuelle
   */
  pagination: U2000Pagination | null;
  /**
   * Nombre total de notifications
   */
  totalCount: number;
  /**
   * Indique s'il y a plus de notifications à charger
   */
  hasMore: boolean;
  /**
   * Rafraîchir la liste des notifications
   */
  refresh: () => Promise<void>;
  /**
   * Charger plus de notifications (pagination)
   */
  loadMore: () => Promise<void>;
  /**
   * Marquer une notification comme lue
   */
  markAsRead: (id: string) => Promise<void>;
  /**
   * Marquer une notification comme non lue
   */
  markAsUnread: (id: string) => Promise<void>;
  /**
   * Marquer toutes les notifications comme lues
   */
  markAllAsRead: () => Promise<void>;
  /**
   * Supprimer une notification
   */
  deleteNotification: (id: string) => Promise<void>;
  /**
   * Récupérer le compteur de non lues
   */
  refreshUnreadCount: () => Promise<void>;
}

const U2000NotificationContext = createContext<U2000NotificationContextType | undefined>(undefined);

export function useU2000NotificationContext(): U2000NotificationContextType {
  const context = useContext(U2000NotificationContext);
  if (!context) {
    throw new Error("useU2000NotificationContext must be used within U2000NotificationProvider");
  }
  return context;
}

interface U2000NotificationProviderProps {
  children: ReactNode;
}

const DEFAULT_PAGE_SIZE = 20;

/** Login complet (après login/refresh), pas prelogin — évite 401 sur U2105 et 4401 WebSocket. */
function isFullyAuthenticated(auth: { isAuthenticated?: boolean; token?: string | null; securityTags?: string[]; user?: { userName?: string | null } }) {
  return (
    !!auth.isAuthenticated &&
    !!auth.token &&
    ((auth.securityTags?.length ?? 0) > 0 || !!(auth.user?.userName))
  );
}

export function U2000NotificationProvider({ children }: U2000NotificationProviderProps) {
  const { t } = useTranslation("common");
  const { auth } = useU2000AuthContext();
  const { on } = useU2000WebSocketContext();
  const axios = useU2000AxiosAuthenticated();
  const fullyAuthenticated = isFullyAuthenticated(auth);

  const [notifications, setNotifications] = useState<U2000Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<U2000Pagination | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const isLoadingRef = useRef<boolean>(false);
  /** Ids déjà traités via WS — idempotence badge (BL-0435). */
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());

  /**
   * Récupérer le compteur de notifications non lues
   */
  const refreshUnreadCount = useCallback(async () => {
    if (!fullyAuthenticated) return;

    try {
      const response = await doGetUnreadCount(axios);
      setUnreadCount(response.data.count);
    } catch (err: any) {
      // Gérer les erreurs 500/404 (backend pas encore implémenté) de manière silencieuse
      const status = err?.response?.status || err?.status;
      if (status === 500 || status === 404) {
        if (import.meta.env.DEV) {
          console.warn("[Notification] Backend U2105 pas encore disponible pour getUnreadCount (status:", status, ")");
        }
        // Garder le compteur à 0 si le backend n'est pas disponible
        setUnreadCount(0);
      } else {
        const apiError = U2000HandleApiError(err as Parameters<typeof U2000HandleApiError>[0], t, "common");
        console.error("[Notification] Erreur lors de la récupération du compteur:", apiError);
      }
      // Ne pas bloquer l'application si le compteur échoue
    }
  }, [fullyAuthenticated, axios, t]);

  /**
   * Charger les notifications avec pagination
   */
  const loadNotifications = useCallback(
    async (page: number = 1, reset: boolean = false) => {
      if (!fullyAuthenticated || isLoadingRef.current) return;

      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const searchPayload: U2000SearchPayload = {
          filter: {
            criterias: [],
            fields: [],
            sortedFields: [{ name: "timestamp", sortOrder: "DESC" }], // Plus récentes en premier
          },
          pagination: {
            page,
            size: DEFAULT_PAGE_SIZE,
          },
        };

        const response = await doSearch(axios, searchPayload);
        const result = response.data;

        if (reset) {
          setNotifications(result.results);
          seenNotificationIdsRef.current = new Set(result.results.map((n) => n.id));
        } else {
          setNotifications((prev) => {
            // Éviter les doublons
            const existingIds = new Set(prev.map((n) => n.id));
            const newNotifications = result.results.filter((n) => !existingIds.has(n.id));
            for (const n of result.results) {
              seenNotificationIdsRef.current.add(n.id);
            }
            return [...prev, ...newNotifications];
          });
        }

        setPagination(result.input.pagination);
        setTotalCount(result.queryRowsCount);
        setHasMore(result.results.length === DEFAULT_PAGE_SIZE && result.results.length < result.queryRowsCount);
      } catch (err: any) {
        // Gérer les erreurs 500 (backend pas encore implémenté) de manière silencieuse
        const status = err?.response?.status || err?.status;
        if (status === 500 || status === 404) {
          if (import.meta.env.DEV) {
            console.warn("[Notification] Backend U2105 pas encore disponible (status:", status, "). Le centre de notification sera disponible une fois le backend implémenté.");
          }
          // Ne pas afficher d'erreur si le backend n'est pas encore implémenté
          setError(null);
        } else {
          const apiError = U2000HandleApiError(err as Parameters<typeof U2000HandleApiError>[0], t, "common");
          const errorMessage = apiError.genericErrors?.[0]?.text || apiError.genericErrors?.[0] || "Erreur lors du chargement des notifications";
          setError(typeof errorMessage === "string" ? errorMessage : errorMessage.text || "Erreur lors du chargement des notifications");
          console.error("[Notification] Erreur lors du chargement:", apiError);
        }
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [fullyAuthenticated, axios, t]
  );

  /**
   * Rafraîchir la liste des notifications
   */
  const refresh = useCallback(async () => {
    await Promise.all([loadNotifications(1, true), refreshUnreadCount()]);
  }, [loadNotifications, refreshUnreadCount]);

  /**
   * Charger plus de notifications
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading || !pagination) return;
    await loadNotifications(pagination.page + 1, false);
  }, [hasMore, isLoading, pagination, loadNotifications]);

  /**
   * Marquer une notification comme lue
   */
  const markAsRead = useCallback(
    async (id: string) => {
      try {
        await doMarkAsRead(axios, id);
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        const apiError = U2000HandleApiError(err as Parameters<typeof U2000HandleApiError>[0], t, "common");
        console.error("[Notification] Erreur lors du marquage comme lue:", apiError);
        throw err;
      }
    },
    [axios, t]
  );

  /**
   * Marquer une notification comme non lue
   */
  const markAsUnread = useCallback(
    async (id: string) => {
      try {
        await doMarkAsUnread(axios, id);
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
        setUnreadCount((prev) => prev + 1);
      } catch (err) {
        const apiError = U2000HandleApiError(err as Parameters<typeof U2000HandleApiError>[0], t, "common");
        console.error("[Notification] Erreur lors du marquage comme non lue:", apiError);
        throw err;
      }
    },
    [axios, t]
  );

  /**
   * Marquer toutes les notifications comme lues
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await doMarkAllAsRead(axios);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      const apiError = U2000HandleApiError(err as Parameters<typeof U2000HandleApiError>[0], t, "common");
      console.error("[Notification] Erreur lors du marquage de toutes comme lues:", apiError);
      throw err;
    }
  }, [axios, t]);

  /**
   * Supprimer une notification
   */
  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        await doDeleteNotification(axios, id);
        const deletedNotification = notifications.find((n) => n.id === id);
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        if (deletedNotification && !deletedNotification.isRead) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        setTotalCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        const apiError = U2000HandleApiError(err as Parameters<typeof U2000HandleApiError>[0], t, "common");
        console.error("[Notification] Erreur lors de la suppression:", apiError);
        throw err;
      }
    },
    [axios, notifications, t]
  );

  /**
   * Écouter les nouvelles notifications via WebSocket (uniquement après login complet).
   * BL-0435 : filtre org, dédup id, refreshUnreadCount (pas de ++ hors dédup).
   */
  useEffect(() => {
    if (!fullyAuthenticated) return;

    if (import.meta.env.DEV) {
      console.log("[Notification] Écoute WebSocket notification:new démarrée");
    }

    const unsubscribe = on("notification:new", (message) => {
      if (import.meta.env.DEV) {
        console.log("[Notification] Message WebSocket notification:new reçu:", message);
        console.log("[Notification] Type du message:", typeof message, "Keys:", Object.keys(message || {}));
      }

      if (!isNotificationMessage(message)) {
        console.warn("[Notification] Message WebSocket reçu mais non valide:", message);
        return;
      }

      const currentOrgId = auth.user?.organizationId;
      if (!u2000ShouldAcceptNotificationForOrg(message.organizationId, currentOrgId)) {
        if (import.meta.env.DEV) {
          console.log(
            "[Notification] Message ignoré (org ≠ session):",
            message.organizationId,
            "vs",
            currentOrgId,
          );
        }
        return;
      }

      const notificationType = message.notificationType || message.typeNotification || "system";
      const userId = message.userId || auth.user?.userName || "";
      const version = message.version || 0;

      const notification: U2000Notification = {
        id: message.id,
        version: version,
        userId: userId,
        organizationId: message.organizationId,
        type: notificationType as U2000Notification["type"],
        importance: message.importance,
        title: message.title,
        message: message.message,
        timestamp: message.timestamp,
        isRead: message.isRead,
        fileUrl: message.fileUrl,
        fileMimeType: message.fileMimeType,
        linkUrl: message.linkUrl,
        metadata: message.metadata,
      };

      const isNew = u2000IsNewNotificationId(
        notification.id,
        seenNotificationIdsRef.current,
        seenNotificationIdsRef.current,
      );

      if (!isNew) {
        setNotifications((prev) => {
          const exists = prev.some((n) => n.id === notification.id);
          if (exists) {
            return prev.map((n) => (n.id === notification.id ? notification : n));
          }
          return prev;
        });
        return;
      }

      seenNotificationIdsRef.current.add(notification.id);

      setNotifications((prev) => {
        const exists = prev.some((n) => n.id === notification.id);
        if (exists) {
          return prev.map((n) => (n.id === notification.id ? notification : n));
        }
        if (import.meta.env.DEV) {
          console.log("[Notification] Nouvelle notification ajoutée:", notification.id);
        }
        return [notification, ...prev];
      });

      if (!notification.isRead) {
        setTotalCount((prev) => prev + 1);
        // Vérité API plutôt qu'un ++ local durable (BL-0435)
        void refreshUnreadCount();
      }
    });

    return unsubscribe;
  }, [fullyAuthenticated, on, auth.user?.organizationId, auth.user?.userName, refreshUnreadCount]);

  /**
   * Charger les notifications au montage et lors du login complet (pas en prelogin, évite 401)
   * Note: Si le backend U2105 n'est pas encore implémenté (erreur 500/404),
   * le chargement échouera silencieusement et les notifications WebSocket continueront de fonctionner
   */
  useEffect(() => {
    if (fullyAuthenticated) {
      // Charger les notifications en arrière-plan (ne bloque pas si le backend n'est pas disponible)
      refresh().catch((err: any) => {
        // Erreur déjà gérée dans refreshUnreadCount et loadNotifications
        // Juste éviter que la promesse non gérée apparaisse dans la console
        const status = err?.response?.status || err?.status;
        if (status !== 500 && status !== 404) {
          // Seules les erreurs autres que 500/404 sont vraiment problématiques
          console.error("[Notification] Erreur inattendue lors du refresh:", err);
        }
      });
    } else {
      // Réinitialiser l'état lors de la déconnexion ou en prelogin
      setNotifications([]);
      setUnreadCount(0);
      setPagination(null);
      setTotalCount(0);
      setHasMore(false);
      seenNotificationIdsRef.current.clear();
    }
  }, [fullyAuthenticated, auth.user.organizationId, refresh]);

  const contextValue: U2000NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    error,
    pagination,
    totalCount,
    hasMore,
    refresh,
    loadMore,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    refreshUnreadCount,
  };

  return <U2000NotificationContext.Provider value={contextValue}>{children}</U2000NotificationContext.Provider>;
}
